import { useEffect, useMemo, useState } from 'react'
import type { RawMessage, TestInfoResponse } from '@sensor/shared'
import { fetchInfo } from '../lib/api.js'
import { useLiveMessages } from '../lib/useLiveMessages.js'

const CONNECTION_LABEL: Record<string, string> = {
  live: 'En vivo',
  connecting: 'Conectando…',
  offline: 'Sin conexión',
}

const AUTH_LABEL: Record<RawMessage['auth'], string> = {
  disabled: 'sin token',
  ok: 'token ok',
  missing: 'falta token',
  invalid: 'token inválido',
}

function formatTime(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleTimeString('es-CL', { hour12: false }) + `.${String(date.getMilliseconds()).padStart(3, '0')}`
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  return `${(bytes / 1024).toFixed(1)} KB`
}

/** Resumen de una línea para la tabla: body, o query si el envío fue por GET. */
function preview(message: RawMessage): string {
  const body = message.bodyRaw.trim()
  if (body) return body.replace(/\s+/g, ' ').slice(0, 120)
  if (message.bodyHex) return `binario: ${message.bodyHex.slice(0, 48)}…`

  const query = Object.entries(message.query)
  if (query.length > 0) return query.map(([k, v]) => `${k}=${v}`).join('&').slice(0, 120)

  return '(sin contenido)'
}

function matchesFilter(message: RawMessage, filter: string): boolean {
  if (!filter) return true
  const needle = filter.toLowerCase()
  const haystack = [
    message.remoteIp,
    message.method,
    message.contentType ?? '',
    message.bodyRaw,
    JSON.stringify(message.query),
    JSON.stringify(message.headers),
  ]
    .join(' ')
    .toLowerCase()
  return haystack.includes(needle)
}

function IngestCard({ info }: { info: TestInfoResponse | null }) {
  const [copied, setCopied] = useState<string | null>(null)

  const url = useMemo(() => {
    if (!info) return ''
    return info.ingestUrl.startsWith('http')
      ? info.ingestUrl
      : `${window.location.origin}${info.ingestUrl}`
  }, [info])

  const curl = `curl -X POST ${url} -H "Content-Type: application/json" -d "{\\"imei\\":\\"860123456789012\\",\\"temp\\":23.5}"`

  const copy = async (label: string, text: string) => {
    await navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 1500)
  }

  if (!info) return null

  return (
    <section className="card">
      <div className="card-row">
        <div>
          <span className="label">URL de ingesta</span>
          <code className="url">{url}</code>
        </div>
        <button type="button" onClick={() => void copy('url', url)}>
          {copied === 'url' ? 'Copiado' : 'Copiar URL'}
        </button>
      </div>

      <div className="card-row">
        <div className="grow">
          <span className="label">Prueba rápida</span>
          <code className="url dim">{curl}</code>
        </div>
        <button type="button" onClick={() => void copy('curl', curl)}>
          {copied === 'curl' ? 'Copiado' : 'Copiar curl'}
        </button>
      </div>

      <div className="card-meta">
        <span>Acepta GET, POST y PUT · cualquier content-type</span>
        <span>Máx. {formatBytes(info.maxBodyBytes)} por envío</span>
        <span>Buffer: {info.bufferSize} mensajes</span>
        <span>{info.rawLogEnabled ? 'Copia en disco activada' : 'Sin copia en disco'}</span>
        <span className={info.requireToken ? 'ok' : 'warn'}>
          {info.requireToken ? 'Token de dispositivo requerido' : 'Ingesta abierta (sólo para pruebas)'}
        </span>
      </div>
    </section>
  )
}

function DetailPanel({ message }: { message: RawMessage }) {
  const tabs = [
    message.parsedJson !== null ? 'JSON' : null,
    'Body',
    Object.keys(message.query).length > 0 ? 'Query' : null,
    'Headers',
  ].filter((t): t is string => t !== null)

  const [tab, setTab] = useState(tabs[0] ?? 'Body')

  // Al cambiar de mensaje, volver a la primera pestaña disponible.
  useEffect(() => {
    setTab(message.parsedJson !== null ? 'JSON' : 'Body')
  }, [message.id, message.parsedJson])

  return (
    <aside className="detail">
      <header className="detail-header">
        <div>
          <h2>#{message.id}</h2>
          <span className="dim">{new Date(message.receivedAt).toLocaleString('es-CL')}</span>
        </div>
        <dl className="detail-meta">
          <div>
            <dt>IP</dt>
            <dd>{message.remoteIp}</dd>
          </div>
          <div>
            <dt>Método</dt>
            <dd>{message.method}</dd>
          </div>
          <div>
            <dt>Tipo</dt>
            <dd>{message.contentType ?? '—'}</dd>
          </div>
          <div>
            <dt>Tamaño</dt>
            <dd>{formatBytes(message.bodyBytes)}</dd>
          </div>
          <div>
            <dt>Auth</dt>
            <dd className={message.auth === 'invalid' || message.auth === 'missing' ? 'error' : ''}>
              {AUTH_LABEL[message.auth]}
            </dd>
          </div>
        </dl>
      </header>

      {message.parseError && (
        <p className="alert">
          No se pudo parsear como JSON: <strong>{message.parseError}</strong>
        </p>
      )}
      {message.truncated && <p className="alert">El body se cortó para mostrarlo; el archivo .jsonl tiene el resto.</p>}

      <nav className="tabs">
        {tabs.map((name) => (
          <button
            key={name}
            type="button"
            className={tab === name ? 'active' : ''}
            onClick={() => setTab(name)}
          >
            {name}
          </button>
        ))}
      </nav>

      <div className="detail-body">
        {tab === 'JSON' && <pre>{JSON.stringify(message.parsedJson, null, 2)}</pre>}
        {tab === 'Body' && (
          <pre>{message.bodyRaw || message.bodyHex || '(sin contenido)'}</pre>
        )}
        {tab === 'Query' && <pre>{JSON.stringify(message.query, null, 2)}</pre>}
        {tab === 'Headers' && (
          <table className="kv">
            <tbody>
              {Object.entries(message.headers).map(([key, value]) => (
                <tr key={key}>
                  <td>{key}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </aside>
  )
}

export function Prueba() {
  const { messages, stats, connection, error, clear } = useLiveMessages()
  const [info, setInfo] = useState<TestInfoResponse | null>(null)
  const [filter, setFilter] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)

  useEffect(() => {
    fetchInfo()
      .then(setInfo)
      .catch(() => setInfo(null))
  }, [])

  const visible = useMemo(() => messages.filter((m) => matchesFilter(m, filter)), [messages, filter])
  const selected = useMemo(
    () => visible.find((m) => m.id === selectedId) ?? visible[0] ?? null,
    [visible, selectedId],
  )

  return (
    <div className="page">
      <header className="topbar">
        <div>
          <h1>Prueba de recepción</h1>
          <p className="dim">
            Todo lo que llegue a <code>/api/ingest</code> aparece acá tal cual, sin interpretar.
          </p>
        </div>
        <div className="topbar-right">
          <span className={`status ${connection}`}>
            <i />
            {CONNECTION_LABEL[connection]}
          </span>
          <span className="counter">
            <strong>{stats.total}</strong> recibidos
          </span>
          <button type="button" onClick={() => void clear()}>
            Limpiar
          </button>
        </div>
      </header>

      {error && <p className="alert">{error}</p>}

      <IngestCard info={info} />

      <div className="content">
        <section className="list">
          <div className="list-head">
            <input
              type="search"
              placeholder="Filtrar por IP, contenido, header…"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
            <span className="dim">
              {visible.length} de {messages.length}
            </span>
          </div>

          {visible.length === 0 ? (
            <div className="empty">
              <p>{messages.length === 0 ? 'Esperando el primer mensaje…' : 'Ningún mensaje coincide con el filtro.'}</p>
              {messages.length === 0 && (
                <p className="dim">Envía algo con el curl de arriba o desde el dispositivo.</p>
              )}
            </div>
          ) : (
            <table className="messages">
              <thead>
                <tr>
                  <th>Hora</th>
                  <th>IP</th>
                  <th>Método</th>
                  <th>Tipo</th>
                  <th>Tamaño</th>
                  <th>Contenido</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((message) => (
                  <tr
                    key={message.id}
                    className={selected?.id === message.id ? 'selected' : ''}
                    onClick={() => setSelectedId(message.id)}
                  >
                    <td className="mono">{formatTime(message.receivedAt)}</td>
                    <td className="mono">{message.remoteIp}</td>
                    <td>{message.method}</td>
                    <td className="dim">{message.contentType?.split(';')[0] ?? '—'}</td>
                    <td className="mono">{formatBytes(message.bodyBytes)}</td>
                    <td className="mono preview">
                      {message.parseError && <span className="badge error">JSON inválido</span>}
                      {(message.auth === 'invalid' || message.auth === 'missing') && (
                        <span className="badge error">{AUTH_LABEL[message.auth]}</span>
                      )}
                      {preview(message)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {selected ? <DetailPanel message={selected} /> : <aside className="detail empty-detail" />}
      </div>
    </div>
  )
}
